import { lex } from './lex';

/**
 * The rules are as follows:
 *  1. If it's all alphanumeric do it as `%foo`
 *  2. If it contains weird characters wrap it in `%"some name"` and escape all the double quotes inside of it
 */
export const symbolEraQL = (symbol: string): string =>
  /^[a-zA-Z_0-9]+$/.test(symbol)
    ? `%${symbol}`
    : `%"${symbol.replaceAll(`"`, `\\"`)}"`;

/**
 * Find all symbol reference ranges in an eraql string that match a given symbol name.
 * Uses the same lexer logic as extractSymbolReferencesFromEraql.
 * Handles both formats: %symbol and %"symbol"
 *
 * @param eraql - The eraql string to search in
 * @param symbolName - The symbol name to find
 * @returns Array of ranges [start, end] for each matching symbol reference
 */
export const findSymbolReferenceRanges = (
  eraql: string,
  symbolName: string
): (readonly [number, number])[] => {
  // Ensure eraql is a non-empty string before lexing
  if (
    eraql == null ||
    eraql === undefined ||
    typeof eraql !== 'string' ||
    eraql.trim().length === 0
  ) {
    return [];
  }

  // Ensure symbolName is a valid string
  if (symbolName == null || typeof symbolName !== 'string') {
    return [];
  }

  const ranges: (readonly [number, number])[] = [];

  // Wrap lexer call in try-catch to handle any unexpected errors
  let tokens;
  try {
    tokens = Array.from(lex(eraql));
  } catch (error) {
    // If lexing fails, return empty ranges
    return [];
  }

  for (let i = 0; i < tokens.length; i++) {
    const tokenWithMeta = tokens[i];
    if (!tokenWithMeta) {
      continue;
    }
    const [token, meta] = tokenWithMeta;

    // Handle quoted identifiers (without % prefix)
    // Example: "global expr" -> return range for "global expr" (content only, no quotes)
    if (token.t === 'ident' && meta.range) {
      const tokenText = eraql.slice(meta.range[0], meta.range[1]);
      if (tokenText.startsWith('"') && tokenText.endsWith('"')) {
        const identValue = tokenText.slice(1, -1); // Remove quotes

        // Check if symbolName matches the ident value (without quotes)
        if (identValue === symbolName) {
          // Return range for content only (excluding quotes)
          ranges.push([meta.range[0] + 1, meta.range[1] - 1] as const);
          continue;
        }
      }
    }

    // Handle symbol tokens
    if (token.t === 'symbol' && meta.range) {
      const tokenText = eraql.slice(meta.range[0], meta.range[1]);

      // Check if this is a symbol reference (starts with %)
      if (tokenText.startsWith('%')) {
        // Handle quoted format: %"symbol name" -> symbol name
        // For quoted symbols, the lexer tokenizes % and "symbol name" separately
        if (tokenText === '%' && i + 1 < tokens.length) {
          const nextTokenWithMeta = tokens[i + 1];
          if (!nextTokenWithMeta) {
            continue;
          }
          const [nextToken, nextMeta] = nextTokenWithMeta;
          if (nextToken.t === 'ident' && nextMeta.range) {
            const nextTokenText = eraql.slice(
              nextMeta.range[0],
              nextMeta.range[1]
            );
            // Check if it's a quoted ident that matches our symbol
            if (nextTokenText.startsWith('"') && nextTokenText.endsWith('"')) {
              const identValue = nextTokenText.slice(1, -1); // Remove quotes
              const identWithQuotes = nextTokenText; // Keep quotes

              // Check if symbolName matches the ident value (without quotes) or with quotes
              if (identValue === symbolName || identWithQuotes === symbolName) {
                // This is a quoted symbol reference: %"symbol name"
                // Return range for content only (excluding % and quotes)
                ranges.push([
                  nextMeta.range[0] + 1,
                  nextMeta.range[1] - 1,
                ] as const);
                i++; // Skip the next token since we've processed it
                continue;
              }
            }
          }
        }

        // Handle unquoted format: %symbol (single token, no spaces)
        // Unquoted symbols cannot contain spaces, so we only check the single token
        let extractedSymbolName = token.sym;

        // Remove % prefix if present (token.sym might be "%symbol" or "symbol")
        if (extractedSymbolName.startsWith('%')) {
          extractedSymbolName = extractedSymbolName.slice(1);
        }

        // Check if the extracted symbol name matches (single token only)
        if (extractedSymbolName === symbolName) {
          // Return range for symbol name only (excluding %)
          ranges.push([meta.range[0] + 1, meta.range[1]] as const);
        }
      }
    }
  }

  return ranges;
};
